import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaEye, FaEyeSlash, FaEnvelope, FaPhone, FaStore, FaHospital, FaStethoscope, FaArrowRight, FaShieldAlt, FaHeartbeat, FaPills, FaMagic, FaStar, FaRocket, FaGem } from 'react-icons/fa';
import '../../../styles/password-input.css';
import { useDispatch, useSelector } from 'react-redux';
import { storeLogin, clearError } from '../../../redux/Slices/storeAuthSlice';
import { useNavigate } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import axios from 'axios';
import { getWebFcmToken } from '../../../firebase/webPush';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [shake, setShake] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userType, setUserType] = useState('store'); // store, clinic, doctor

    // بيانات الصور والأنيميشن لكل نوع مستخدم
    const userTypeData = {
        store: {
            image: '/placeholder.jpg', // يمكن استبدالها بصورة صيدلية
            title: 'المتاجر والصيدليات',
            subtitle: 'إدارة منتجاتك وطلباتك بسهولة',
            color: '#184194',
            gradient: 'linear-gradient(135deg, #184194 0%, #1e40af 100%)',
            icon: FaStore,
            features: ['إدارة المنتجات', 'تتبع الطلبات', 'إحصائيات المبيعات', 'إدارة الفروع', 'نظام الطلبات المتقدم', 'تقارير مفصلة']
        },
        clinic: {
            image: '/placeholder.jpg', // يمكن استبدالها بصورة عيادة
            title: 'العيادات الطبية',
            subtitle: 'نظام إدارة شامل للعيادات',
            color: '#059669',
            gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            icon: FaHospital,
            features: ['إدارة المواعيد', 'سجلات المرضى', 'التقارير الطبية']
        },
        doctor: {
            image: '/placeholder.jpg', // يمكن استبدالها بصورة طبيب
            title: 'الأطباء',
            subtitle: 'منصة متخصصة للأطباء',
            color: '#dc2626',
            gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            icon: FaStethoscope,
            features: ['إدارة المرضى', 'المواعيد', 'الاستشارات']
        }
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading: storeLoading, error: storeError } = useSelector((state) => state.storeAuth);

    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    useEffect(() => {
        if (storeError) {
            setShake(true);
            showErrorToast(storeError);
            setTimeout(() => setShake(false), 500);
        }
    }, [storeError]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!phone || !password) {
            setShake(true);
            showErrorToast('يجب إدخال رقم الهاتف وكلمة المرور');
            setTimeout(() => setShake(false), 500);
            setLoading(false);
            return;
        }

        dispatch(clearError());

        try {
            let loginEndpoint = '';
            let storageKey = '';
            let redirectPath = '';

            // تحديد الـ endpoint حسب نوع المستخدم
            if (userType === 'store') {
                loginEndpoint = '/stores/store_login';
                storageKey = 'storekhadamat';
                redirectPath = '/';
            } else if (userType === 'clinic') {
                loginEndpoint = '/clinics/login';
                storageKey = 'clinicKhadamat';
                redirectPath = '/';
            } else if (userType === 'doctor') {
                loginEndpoint = '/doctor/login';
                storageKey = 'doctorKhadamat';
                redirectPath = '/';
            }

            // إضافة +20 قبل رقم الهاتف
            const phoneWithPrefix = `+20${phone}`;
            
            // بناء البيانات المرسلة
            const loginData = {
                phone: phoneWithPrefix,
                password,
                role: userType
            };
            
            // إرسال FCM token لكل أنواع المستخدمين ليستطيع الباك إند الاشتراك في topic (مثل all_clients)
            try {
                const webFcmToken = await getWebFcmToken();
                loginData.web_fcm_token = webFcmToken || '';
            } catch {
                loginData.web_fcm_token = '';
            }
            
            const response = await axios.post(`${BASE_URL}${loginEndpoint}`, loginData);

            if (response.data.status === 'success' || response.data.statusCode === 200) {
                const userData = {
                    ...response.data.result,
                    role: response.data.result.role || userType // استخدام الـ role من API أو userType كـ fallback
                };

                sessionStorage.setItem(storageKey, JSON.stringify(userData));

                // التنقل أولاً
                window.location.href = redirectPath;
                
                // إظهار التوست بعد التنقل
                setTimeout(() => {
                    showSuccessToast('تم تسجيل الدخول بنجاح');
                }, 100);
            } else {
                throw new Error(response.data.message || 'فشل في تسجيل الدخول');
            }
        } catch (err) {
            showErrorToast(err.response?.data?.message || err.message || 'فشل في تسجيل الدخول');
        } finally {
            setLoading(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0 auth-page"
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                overflow: 'hidden'
            }}>
            
            {/* خلفية متحركة خفيفة */}
            <div className="position-fixed w-100 h-100" style={{ zIndex: 0 }}>
                {/* دائرة واحدة متحركة */}
                        <motion.div
                    animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [0.1, 0.15, 0.1]
                    }}
                    transition={{ 
                        duration: 8, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                    }}
                            style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                            borderRadius: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                />
            </div>

            <div className="row w-100 h-100 m-0" style={{ zIndex: 1 }}>
                {/* جزء الصورة */}
                <div className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center position-relative">
                    <div className="text-center text-white px-5">
                        <div className="mb-4">
                                <div className="mb-4 position-relative">
                                    <div 
                                        className="rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                        style={{
                                            width: '120px',
                                            height: '120px',
                                            background: userTypeData[userType].gradient,
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                            zIndex: 2
                                }}
                                        >
                                            {React.createElement(userTypeData[userType].icon, { size: 60 })}
                                    </div>
                                </div>

                                <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
                                    {userTypeData[userType].title}
                                </h2>
                                
                                <p className="mb-4 position-relative" style={{ fontSize: '1.2rem', opacity: 0.9 }}>
                                    <span style={{ display: 'inline-block', marginLeft: '10px' }}>
                                        <FaStar />
                                    </span>
                                    {userTypeData[userType].subtitle}
                                </p>

                                <div className="text-start">
                                    {userTypeData[userType].features.map((feature, index) => (
                                        <div key={feature} className="d-flex align-items-center mb-3 position-relative">
                                            <div className="mx-3">
                                                <FaArrowRight style={{ fontSize: '0.9rem' }} />
                                            </div>
                                            
                                            <span style={{ fontSize: '1.1rem' }}>
                                                {feature}
                                            </span>
                                            
                                            <div className="ms-2" style={{ fontSize: '0.8rem' }}>
                                                <FaStar />
                                            </div>
                                        </div>
                            ))}
                                </div>
                            </div>
                        </div>
                    </div>

                {/* جزء الفورم */}
                        <div className="col-lg-6 d-flex align-items-center justify-content-center" style={{ padding: '2rem 0' }}>
                            <div className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                        style={{
                            width: '100%',
                            maxWidth: '500px',
                            border: 'none',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            backgroundColor: 'white',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
                            position: 'relative'
                        }}>

                        <div className="card-body p-5">
                            <div className="text-center mb-4">
                                <img
                                    src="/logokhadamat.png"
                                    alt="Khadamat Logo"
                                    style={{
                                        height: '80px',
                                        width: 'auto'
                                    }}
                                />
                            </div>

                            <h3 className="text-center mb-3 fw-bold" style={{ color: '#333', fontSize: '24px' }}>
                                مرحباً بعودتك
                            </h3>

                            {/* User Type Selection */}
                            <div className="mb-4">
                                <div className="d-flex gap-4 justify-content-center" role="group" style={{ flexWrap: 'wrap' }}>
                                    {Object.entries(userTypeData).map(([type, data]) => (
                                        <button
                                            key={type}
                                            type="button"
                                            className={`btn user-type-btn ${userType === type ? 'active' : ''}`}
                                            onClick={() => setUserType(type)}
                                            style={{ 
                                                flex: '1',
                                                background: userType === type ? 'linear-gradient(135deg, #184194 0%, #1e40af 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                                color: userType === type ? 'white' : '#64748b',
                                                border: 'none',
                                                borderRadius: '15px',
                                                padding: '15px 20px',
                                                fontWeight: '600',
                                                fontSize: '0.9rem',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: userType === type ? '0 4px 12px rgba(24, 65, 148, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                            <data.icon size={20} />
                                            <span>{data.title}</span>
                                        </button>
                            ))}
                        </div>
                            </div>

                    <p className="text-center mb-4"
                                    style={{
                                        color: userTypeData[userType].color,
                                        fontSize: '1.1rem',
                                        fontWeight: '500'
                                    }}>
                                    {userTypeData[userType].subtitle}
                                </p>

                            <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                                    <label htmlFor="loginField" className="form-label fw-medium" style={{ color: '#333' }}>
                                رقم الهاتف
                            </label>
                                    <div 
                                        className="input-group position-relative" 
                                        style={{direction:"ltr"}}
                                    >
                                        <span className="input-group-text" style={{ 
                                            fontSize: '16px', 
                                            fontWeight: '500',
                                            background: '#f8f9fa',
                                            color: '#6c757d',
                                            border: '2px solid #e2e8f0'
                                        }}>
                                            +20
                                        </span>
                                <input
                                    type="tel"
                                            className="form-control"
                                            id="loginField"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="10xxxxxxxxx"
                                    required
                                            style={{
                                                borderLeft: 'none',
                                                borderRadius: '0 12px 12px 0',
                                                border: '2px solid #e2e8f0',
                                                padding: '12px 16px',
                                                fontSize: '16px',
                                                transition: 'all 0.3s ease'
                                            }}
                                        />
                                </div>
                        </div>

                        <div className="mb-4">
                                    <label htmlFor="password" className="form-label fw-medium" style={{ color: '#333' }}>
                                كلمة المرور
                            </label>
                                    <div 
                                        className="input-group password-input" 
                                        style={{direction:"ltr"}}
                                    >
                                <input
                                    type={showPassword ? "text" : "password"}
                                            className="form-control"
                                            id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="أدخل كلمة المرور"
                                    required
                                            style={{
                                                borderRadius: '12px 0 0 12px',
                                                border: '2px solid #e2e8f0',
                                                padding: '12px 16px',
                                                fontSize: '16px',
                                                transition: 'all 0.3s ease'
                                            }}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                            className="input-group-text"
                                    onClick={() => setShowPassword(!showPassword)}
                                            style={{ 
                                                cursor: 'pointer', 
                                                borderRadius: '0 12px 12px 0',
                                                background: '#f8f9fa',
                                                color: '#6c757d',
                                                border: '2px solid #e2e8f0',
                                                transition: 'all 0.3s ease'
                                            }}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                                </div>
                        </div>

                        {userType === 'store' && (
                        <div className="mb-3">
                                <div className="text-center">
                            <button
                                type="button"
                                        className="btn btn-link text-decoration-none p-0"
                                onClick={() => navigate('/store-forgot-password')}
                                        style={{ color: '#184194', fontSize: '0.9rem' }}>
                                نسيت كلمة المرور؟
                            </button>
                                </div>
                        </div>
                        )}

                        <button
                            type="submit"
                                    className="btn w-100 text-white py-3 fw-medium position-relative overflow-hidden"
                                    style={{
                                        background: userTypeData[userType].gradient,
                                        border: 'none',
                                        borderRadius: '15px',
                                        fontSize: '1.1rem',
                                        marginTop: '1rem',
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    disabled={loading}>
                                {loading ? (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                            className="d-flex align-items-center justify-content-center"
                                        >
                                            <LoadingSpinner />
                                            <span className="ms-2">جاري تسجيل الدخول...</span>
                                    </motion.div>
                                ) : (
                                        <motion.span
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                            className="d-flex align-items-center justify-content-center"
                                        >
                                            تسجيل الدخول
                                        </motion.span>
                                    )}
                        </button>
                            </form>

                    {userType === 'store' && (
                    <div className="mt-4 text-center">
                                <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    ليس لديك حساب؟{' '}
                                </span>
                        <button
                                    type="button"
                                    className="btn btn-link text-decoration-none p-0"
                                    onClick={() => navigate('/store-register')}
                                    style={{ 
                                        color: userTypeData[userType].color, 
                                        fontSize: '0.9rem',
                                        fontWeight: '600'
                                    }}
                        >
                            إنشاء حساب جديد
                        </button>
                            </div>
                    )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// CSS محسن للتصميم الجديد
const style = document.createElement('style');
style.textContent = `
    .auth-page {
        font-family: 'Montserrat', sans-serif;
    }

    .user-type-btn {
        position: relative;
        overflow: hidden;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .user-type-btn::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.5s;
    }
    
    .user-type-btn:hover::before {
        left: 100%;
    }
    
    .user-type-btn.active::before {
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    }
    
    /* تحسينات للحقول */
    .form-control:focus {
        border-color: var(--user-type-color, #184194) !important;
        box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25) !important;
    }
    
    /* أنيميشن للخلفية */
    @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-20px); }
    }
    
    .floating-element {
        animation: float 6s ease-in-out infinite;
    }
    
    /* تحسينات للشاشات الصغيرة */
    @media (max-width: 768px) {
        .auth-page .col-lg-6:first-child {
            display: none !important;
        }
        
        .auth-page .col-lg-6:last-child {
            padding: 3rem 1rem !important;
        }
    }
    
    /* تحسينات للشاشات المتوسطة */
    @media (min-width: 769px) and (max-width: 991px) {
        .auth-page .col-lg-6:last-child {
            padding: 2.5rem 1rem !important;
        }
    }
    
    /* تأثيرات بصرية إضافية */
    .card {
        backdrop-filter: blur(10px);
        background: rgba(255, 255, 255, 0.95) !important;
    }
    
    /* تحسينات للأزرار */
    .btn {
        transition: all 0.3s ease;
    }

    .btn:hover {
        transform: translateY(-2px);
    }
    
    /* تحسينات للنصوص */
    .form-label {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
    
    /* تأثيرات التركيز */
    .input-group:focus-within {
        transform: scale(1.02);
        transition: transform 0.2s ease;
    }
`;
document.head.appendChild(style);

export default LoginPage;
